import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '../../services/authentication.service';
import * as Chart from 'chart.js';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})

export class DashboardComponent implements OnInit, AfterViewInit {
  public loggedInUser: any;
  public subscriptions = 0;
  public total_users = 0;
  public totalInactiveUsers = 0;
  public totalRevenue = 0;
  public revenueByCurrency: { [currency: string]: number } = {};
  public goldPlanPrice = 0;
  public popularPlanPrice = 0;
  public standardPlanPrice = 0;
  public currentDate = new Date();
  public curmonth: string = (this.currentDate.getMonth() + 1).toString();
  public curyear: string = (this.currentDate.getFullYear()).toString();
  public exchangeRate = 1;
  public exchangeRates: { [currency: string]: number } = { USD: 1 };
  public revenueChart: any;
  public subscriptionChart: any;
  public premiumSubscribers = 0;
  public plusSubscribers = 0;
  public boostSubscribers = 0;
  public superLikeSubscribers = 0;
  public premiumPlanRevenue = 0;
  public plusPlanRevenue = 0;
  public boostPlanRevenue = 0;
  public superLikePlanRevenue = 0;
  public selectedOption = 'bar';
  public selectedOptionforSub = 'bar';
  public isLoading = true;
  public selectedChartTab = 'revenue';
  public stats: Array<any> = [];
  public managementCards: Array<any> = [];
  public subscriptionPlanNames: Array<string> = [];

  constructor(private authenticationService: AuthenticationService,
              private route: ActivatedRoute,
              private http: HttpClient) { }

  ngOnInit(): void {
    this.loggedInUser = JSON.parse(localStorage.getItem('user') || 'null');
    this.managementCards = [
      { title: 'Manage Accounts', description: 'Activate or deactivate user profiles.', route: '/users', label: 'Manage', icon: 'fa-users', iconClass: 'white' },
      { title: 'Manage Subscriptions', description: 'Create or edit subscription plans.', route: '/subscription-plans', label: 'Manage', icon: 'fa-credit-card', iconClass: 'teal' },
      { title: 'Manage Content', description: 'Update or delete app content.', route: '/manage-content', label: 'Manage', icon: 'fa-cog', iconClass: 'indigo' },
      { title: 'Revenue History', description: 'Review growth and monthly performance.', route: '/revenue', label: 'View stats', icon: 'fa-line-chart', iconClass: 'amber' }
    ];
    this.buildStats();
    this.fetchExchangeRate();
    this.getDashboardData();
  }

  private fetchExchangeRate(): void {
    this.http.get('https://open.er-api.com/v6/latest/USD').pipe(
      catchError(() => of({ rates: { NGN: 1 } }))
    ).subscribe((response: any) => {
      if (response && response.rates) {
        this.exchangeRates = { USD: 1, ...response.rates };
        this.exchangeRate = this.exchangeRates.NGN || 1;
        this.buildStats();
      }
    });
  }

  ngAfterViewInit(): void {
    this.renderActiveChart();
  }

  onOptionSelected(value: string) {
    this.selectedOption = value;
    this.createChart(this.premiumPlanRevenue, this.plusPlanRevenue, this.boostPlanRevenue, this.superLikePlanRevenue, value);
  }

  onOptionSelectedForSubs(value: string) {
    this.selectedOptionforSub = value;
    this.createChartForSubscription(this.premiumSubscribers, this.plusSubscribers, this.boostSubscribers, this.superLikeSubscribers, this.selectedOptionforSub);
  }

  selectChartTab(tab: string): void {
    this.selectedChartTab = tab;
    this.renderActiveChart();
  }

  getDashboardData() {
    this.isLoading = true;

    forkJoin({
      dashboard: this.authenticationService.dashBoardData().pipe(catchError(() => of(null))),
      revenue: this.authenticationService.getRevenueReport(this.curmonth, this.curyear).pipe(catchError(() => of(null)))
    }).subscribe(({ dashboard, revenue }) => {
      const dashboardData = dashboard && dashboard.data ? dashboard.data : dashboard;
      const revenueData = revenue && revenue.data ? revenue.data : revenue;

      if (dashboardData) {
        this.total_users = dashboardData.totalUsers ? dashboardData.totalUsers : 0;
        this.subscriptions = dashboardData.totalSubscriptions && dashboardData.totalSubscriptions[0] && dashboardData.totalSubscriptions[0].activeSubscribers ? dashboardData.totalSubscriptions[0].activeSubscribers : 0;
        this.totalInactiveUsers = dashboardData.totalInactiveUsers ? dashboardData.totalInactiveUsers : 0;
        this.premiumSubscribers = dashboardData.premiumSubscribers ? dashboardData.premiumSubscribers : 0;
        this.plusSubscribers = dashboardData.plusSubscribers ? dashboardData.plusSubscribers : 0;
        this.boostSubscribers = dashboardData.boostSubscribers ? dashboardData.boostSubscribers : 0;
        this.superLikeSubscribers = dashboardData.superLikeSubscribers ? dashboardData.superLikeSubscribers : 0;
      }

      if (revenueData) {
        this.revenueByCurrency = this.normalizeRevenueByCurrency(revenueData.revenueByCurrency);
        const currencies = Object.keys(this.revenueByCurrency);
        const planRevenueMap = revenueData && revenueData.planRevenue && Object.keys(revenueData.planRevenue).length
          ? revenueData.planRevenue
          : revenueData && revenueData.transactions ? this.groupRevenueByPlanFromTransactions(revenueData.transactions) : {};

        this.totalRevenue = currencies.length === 1
          ? this.revenueByCurrency[currencies[0]]
          : revenueData.totalRevenue !== null && revenueData.totalRevenue !== undefined
            ? Number(revenueData.totalRevenue) || 0
            : this.getTotalFromRevenueMap(planRevenueMap);

        this.premiumPlanRevenue = this.sumByPlanPattern(planRevenueMap, ['premium']);
        this.plusPlanRevenue = this.sumByPlanPattern(planRevenueMap, ['plus']);
        this.boostPlanRevenue = this.sumByPlanPattern(planRevenueMap, ['boost']);
        this.superLikePlanRevenue = this.sumByPlanPattern(planRevenueMap, ['superlike', 'super_like']);
      }

      this.buildStats();
      this.renderActiveChart();
      this.isLoading = false;
    });
  }

  private buildStats(): void {
    this.stats = [
      {
        label: 'Registered Users',
        value: this.formatNumber(this.total_users),
        meta: '+5% this month',
        icon: 'fa-user',
        iconClass: 'white'
      },         
      {
        label: 'Active Subscribers',
        value: this.formatNumber(this.subscriptions),
        meta: 'Healthy renewal flow',
        icon: 'fa-user',
        iconClass: 'teal'
      },
      {
        label: 'Monthly Revenue',
        value: this.formatRevenue(this.totalRevenue),
        meta: 'Updated this month',
        icon: 'fa-usd',
        iconClass: 'amber'
      },
      {
        label: 'Inactive Profiles',
        value: this.formatNumber(this.totalInactiveUsers),
        meta: 'Needs follow-up',
        icon: 'fa-user-times',
        iconClass: 'indigo'
      }
    ];
  }

  private renderCharts(): void {
    this.createChart(this.premiumPlanRevenue, this.plusPlanRevenue, this.boostPlanRevenue, this.superLikePlanRevenue, this.selectedOption);
    this.createChartForSubscription(this.premiumSubscribers, this.plusSubscribers, this.boostSubscribers, this.superLikeSubscribers, this.selectedOptionforSub);
  }

  private renderActiveChart(): void {
    this.renderCharts();
  }

  private formatNumber(value: number): string {
    return Number(value || 0).toLocaleString('en-US');
  }

  public updateExchangeRate(event: any): void {
    const rate = parseFloat(event.target.value);
    this.exchangeRate = rate && rate > 0 ? rate : 1;
    this.exchangeRates.NGN = this.exchangeRate;
    this.buildStats();
  }

  private formatCurrency(value: number): string {
    return `$${Number(value || 0).toLocaleString('en-US')}`;
  }

  private formatRevenue(value: number): string {
    const currencies = Object.keys(this.revenueByCurrency);
    const dollarRevenue = currencies.reduce((total, currency) => {
      const rate = currency === 'USD' ? 1 : this.exchangeRates[currency] || 1;
      return total + this.revenueByCurrency[currency] / rate;
    }, currencies.length ? 0 : (Number(value) || 0) / this.exchangeRate);
    return this.formatCurrency(dollarRevenue);
  }

  private normalizeRevenueByCurrency(value: any): { [currency: string]: number } {
    if (!value || typeof value !== 'object') {
      return {};
    }

    return Object.keys(value).reduce((totals, currency) => {
      const amount = Number(value[currency]);
      if (!isNaN(amount)) {
        totals[currency] = amount;
      }
      return totals;
    }, {} as { [currency: string]: number });
  }

  private normalizeRevenueKey(value: any): string {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  }

  private groupRevenueByPlanFromTransactions(transactions: any[] = []): { [key: string]: number } {
    return (transactions || []).reduce((totals, transaction) => {
      if (!transaction || !transaction.basePlanId) {
        return totals;
      }
      const key = String(transaction.basePlanId).trim();
      const amount = Number(transaction.chargedAmount) || 0;
      totals[key] = (totals[key] || 0) + amount;
      return totals;
    }, {} as { [key: string]: number });
  }

  private getTotalFromRevenueMap(revenueMap: { [key: string]: number } = {}): number {
    return Object.keys(revenueMap).reduce((sum, key) => sum + (Number(revenueMap[key]) || 0), 0);
  }

  private sumByPlanPattern(revenueMap: { [key: string]: number } = {}, patterns: string[]): number {
    if (!revenueMap || !Object.keys(revenueMap).length) {
      return 0;
    }

    return Object.keys(revenueMap).reduce((sum, key) => {
      const normalizedKey = this.normalizeRevenueKey(key);
      const matches = patterns.some((pattern) => {
        const normalizedPattern = this.normalizeRevenueKey(pattern);
        return normalizedKey.includes(normalizedPattern) || normalizedPattern.includes(normalizedKey);
      });

      return matches ? sum + (Number(revenueMap[key]) || 0) : sum;
    }, 0);
  }

  private destroyChart(chartRef: any): void {
    if (chartRef) {
      chartRef.destroy();
    }
  }

  createChart(premiumPlanRevenue: any, plusPlanRevenue: any, boostPlanRevenue: any, superLikePlanRevenue: any, type: any): void {
    const revenueCanvas = document.getElementById('myChart') as HTMLCanvasElement | null;
    if (!revenueCanvas) {
      return;
    }

    this.destroyChart(this.revenueChart);
    this.revenueChart = new Chart(revenueCanvas, {
      type,
      data: {
        labels: ['Premium Plan', 'Plus Plan', 'Boost Plan', 'SuperLike Plan'],
        datasets: [{
          label: 'Revenue from Subscriptions',
          data: [premiumPlanRevenue, plusPlanRevenue, boostPlanRevenue, superLikePlanRevenue],
          backgroundColor: [
            '#EC4899',
            '#3B82F6',
            '#10B981',
            '#F59E0B'
          ],
          borderColor: [
            '#EC4899',
            '#3B82F6',
            '#10B981',
            '#F59E0B'
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              fontSize: 10
            },
            gridLines: {
              color: 'rgba(15, 23, 42, 0.06)'
            }
          }],
          xAxes: [{
            ticks: {
              fontSize: 10
            }
          }]
        },
        legend: {
          display: false
        },
        plugins: {
          tooltip: {
            enabled: true
          }
        }
      }
    });
  }

  createChartForSubscription(premiumSubscribers: any, plusSubscribers: any, boostSubscribers: any, superLikeSubscribers: any, type: any): void {
    const subscriptionCanvas = document.getElementById('myChartForSubscription') as HTMLCanvasElement | null;
    if (!subscriptionCanvas) {
      return;
    }

    this.destroyChart(this.subscriptionChart);
    this.subscriptionChart = new Chart(subscriptionCanvas, {
      type,
      data: {
        labels: ['Premium Plan', 'Plus Plan', 'Boost Plan', 'SuperLike Plan'],
        datasets: [{
          label: 'Number of Subscriber',
          data: [premiumSubscribers, plusSubscribers, boostSubscribers, superLikeSubscribers],
          backgroundColor: [
            '#EC4899',
            '#3B82F6',
            '#10B981',
            '#F59E0B'
          ],
          borderColor: [
            '#EC4899',
            '#3B82F6',
            '#10B981',
            '#F59E0B'
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              fontSize: 10
            },
            gridLines: {
              color: 'rgba(15, 23, 42, 0.06)'
            }
          }],
          xAxes: [{
            ticks: {
              fontSize: 10
            }
          }]
        },
        legend: {
          display: false
        },
        plugins: {
          tooltip: {
            enabled: true
          }
        }
      }
    });
  }
}
